import { FC, useEffect } from 'react';
import Button from '~/components/commonUi/Button/Button';
import Fieldset from '~/components/commonUi/Fieldset';
import { CANDIDATE_FIELD_CONFIG_LEFT, CANDIDATE_FIELD_CONFIG_RIGHT } from '~/consts/candidates';
import {
    Candidate,
    CandidatePhoto,
    CandidateAttributeOption,
    CandidateFieldConfig,
    CandidateKey,
    EventProps,
    RenderFieldByTypeParams,
} from '~/definitions/candidates';
import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';
import Loading from '~/components/commonUi/Loading/Loading';
import CandidateImagePreview from './CandidateImagePreview';
import SelectText from '~/components/commonUi/SelectText/SelectText';
import { DELETE_CONFIRMATION_MESSAGE } from '~/consts/candidates';

interface Props {
    title: string;
    candidate?: Candidate;
    renderFieldByType: (
        params: RenderFieldByTypeParams,
        fieldsKeyWithIssues: CandidateKey[],
        isCreateMode: boolean
    ) => JSX.Element | null;
    isCreateMode: boolean;
    onClickDelete: () => void;
    onClickCancel: () => void;
    onFieldValueChange: (event: EventProps) => void;
    candidatePhoto?: CandidatePhoto;
    loadingCandidatePhoto?: boolean;
    onSaveCandidate: () => void;
    fieldsKeyWithIssues: CandidateKey[];
    candidateGenderOptions: CandidateAttributeOption[];
    isDisabled: boolean;
}

const Details: FC<Props> = (props) => {
    const {
        onClickDelete,
        onClickCancel,
        title,
        candidate,
        onFieldValueChange,
        candidatePhoto,
        loadingCandidatePhoto,
    } = props;
    const getFields = (fieldConfig: CandidateFieldConfig[]) => {
        return fieldConfig.map((field) => {
            field.disabled = props.isDisabled;
            let value = candidate?.[field.key as keyof Candidate] ?? '';
            const isPicture = field.id === 'picture';
            if (isPicture) {
                if (loadingCandidatePhoto) {
                    value = ' ';
                    field.disabled = true;
                } else {
                    value = candidatePhoto?.name ?? '';
                    field.disabled = props.isDisabled || false;
                }
            }
            const isCheckbox = field.type === 'checkbox';
            let fieldContainerClassName = 'col-span-7';
            if (isCheckbox) {
                fieldContainerClassName = 'col-span-11';
            } else if (isPicture) {
                fieldContainerClassName += ' h-[45px]';
            }

            let labelClassNames = 'col-span-4 font-normal py-[7px] flex';
            const isFieldError = props.fieldsKeyWithIssues.find((f) => f === field.key);
            if (isFieldError) {
                labelClassNames += ' text-red-500';
            }

            return (
                <div key={field.id} className="mb-4 grid grid-cols-12 gap-4">
                    {!isCheckbox && (
                        <label htmlFor={field.id} className={labelClassNames}>
                            {field.name}
                            {field.isRequired && <span className="text-[red] inline-block ml-[2px]">*</span>}
                        </label>
                    )}
                    <div className={fieldContainerClassName}>
                        {props.renderFieldByType(
                            {
                                fieldConfig: field,
                                value: value,
                                onChange: onFieldValueChange,
                            },
                            props.fieldsKeyWithIssues,
                            props.isCreateMode
                        )}
                        {field.key === 'gender' && (
                            <SelectText
                                id={field.id}
                                testId={field.id}
                                name={field.key}
                                value={value as string}
                                onChange={onFieldValueChange}
                                options={props.candidateGenderOptions}
                                extendedClassName="w-full"
                                allowEmpty={true}
                                disabled={props.isDisabled}
                            />
                        )}
                    </div>
                    {!props.isDisabled && field.id === 'picture' && (
                        <div className="col-span-1 flex items-center pb-3">
                            {loadingCandidatePhoto ? (
                                <Loading testId={'loading-candidate-photo-link'} />
                            ) : (
                                <a
                                    href={candidatePhoto?.url}
                                    target="_blank"
                                    title="Forhåndsvisning"
                                    rel="noreferrer"
                                    onClick={(e) => {
                                        if (!candidatePhoto?.url) {
                                            e.preventDefault();
                                            window.alert('Please choose file to preview');
                                        }
                                    }}
                                >
                                    <img src="/elements/icons/preview.gif" alt="" />
                                </a>
                            )}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <Fieldset title={title}>
            <form method="post" id="candidateData">
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-6">{getFields(CANDIDATE_FIELD_CONFIG_LEFT)}</div>
                    <div className="col-span-12 lg:col-span-6">
                        {getFields(CANDIDATE_FIELD_CONFIG_RIGHT)}
                        <div className="mb-4 grid grid-cols-12 gap-4">
                            <CandidateImagePreview
                                loadingCandidatePhoto={loadingCandidatePhoto}
                                candidatePhoto={candidatePhoto}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <div>
                        {!props.isDisabled && (
                            <Button
                                testId="save-candidate"
                                onClick={props.onSaveCandidate}
                                btnText="Lagre"
                                title="Lagre"
                            />
                        )}
                        {!props.isDisabled && !props.isCreateMode && (
                            <Button
                                testId="delete-candidate"
                                onClick={onClickDelete}
                                btnText="Slett"
                                title="Slett"
                                extendedClassName="ml-[3px]"
                            />
                        )}
                        <Button
                            testId="cancel-change"
                            onClick={onClickCancel}
                            btnText="Avbryt"
                            title="Avbryt"
                            extendedClassName="ml-[3px]"
                        />
                    </div>
                </div>
            </form>
        </Fieldset>
    );
};

const DetailsWithStore = () => {
    const { candidateStore, generalSettingsStore } = getStore();
    const selectedRegion = candidateStore.getSelectedRegion();
    const title = `${selectedRegion.name} - Kandidat`;

    const candidatesHash = JSON.stringify(candidateStore.candidates);

    const onClickDelete = async () => {
        const confirmDelete = window.confirm(DELETE_CONFIRMATION_MESSAGE);
        if (!confirmDelete) {
            return;
        }
        await candidateStore.deleteCandidate();
    };

    useEffect(() => {
        // Memoize the selected candidate for reset back when cancel
        const selectedCandidate = candidateStore.getSelectedCandidate() as Candidate;
        if (selectedCandidate) {
            candidateStore.setSelectedCandidateMemo(selectedCandidate);
            candidateStore.setSelectedCandidateEdit(selectedCandidate);
        }
    }, [candidateStore, candidatesHash]);

    useEffect(() => {
        if (candidateStore.fieldsKeyWithIssues.length) {
            candidateStore.validateCandidateForm();
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps*/
    }, [candidateStore.selectedCandidateEdit]);

    const candidate = candidateStore.selectedCandidateEdit;

    useEffect(() => {
        if (candidate?.picture) {
            candidateStore.fetchCandidatePhoto();
        }
    }, [candidateStore, candidate?.picture]);

    const onSaveCandidate = candidateStore.isCreateMode
        ? candidateStore.createCandidate
        : candidateStore.updateCandidate;

    return (
        <Details
            title={title}
            candidate={candidate}
            candidatePhoto={candidateStore.getCandidatePhoto()}
            renderFieldByType={candidateStore.renderFieldByType}
            isCreateMode={candidateStore.isCreateMode || false}
            onClickDelete={onClickDelete}
            onClickCancel={candidateStore.cancelEdit}
            onFieldValueChange={candidateStore.onFieldValueChange}
            loadingCandidatePhoto={candidateStore.isFetchingCandidatePhoto}
            onSaveCandidate={onSaveCandidate}
            fieldsKeyWithIssues={candidateStore.fieldsKeyWithIssues}
            candidateGenderOptions={candidateStore.candidateGenderOptions}
            isDisabled={generalSettingsStore.isSettingsDisabled}
        />
    );
};

export default observer(DetailsWithStore);
